<template>
  <div class="row g-3">
    <div class="col-12">
      <WidgetCard title="Receita Recorrente Mensal (MRR)">
        <ChartLine :data="mrr" :options="moneyOptions" />
      </WidgetCard>
    </div>
    <div class="col-12">
      <WidgetCard title="Faturamento e Lucro de Consultas">
        <ChartLine :data="revenueProfit" :options="moneyOptions" />
      </WidgetCard>
    </div>
    <div class="col-12">
      <div class="card">
        <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h6 class="mb-0">Faturamento e Lucro (extrato Stripe)</h6>
          <div class="btn-group btn-group-sm" role="group">
            <button
              v-for="g in granularities"
              :key="g.id"
              type="button"
              class="btn"
              :class="stripeGranularity === g.id ? 'btn-primary' : 'btn-outline-secondary'"
              @click="stripeGranularity = g.id"
            >
              {{ g.label }}
            </button>
          </div>
        </div>
        <div class="card-body">
          <p v-if="syncedUntilLabel" class="text-muted small mb-2">
            <i class="fa-solid fa-circle-info me-1"></i>
            Extrato do Stripe sincronizado até {{ syncedUntilLabel }}. Períodos
            depois disso ainda não têm dado importado (aparecem como um
            corte no gráfico, não como zero real).
          </p>
          <div class="widget-chart" style="height: 300px; position: relative">
            <ChartLine :data="stripeSeries" :options="stripeMoneyOptions" />
          </div>
          <ul class="stripe-footnotes text-muted small mb-0 mt-3">
            <li>
              <strong>* Faturamento</strong> — total movimentado na Stripe no
              período. Inclui o valor cheio das sessões, do qual ~80% é
              repassado aos psicólogos.
            </li>
            <li>
              <strong>* Lucro</strong> — o que fica no Psiapp depois do
              repasse, das taxas da Stripe e dos estornos. Não desconta
              custos da empresa.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { COLORS, moneyBR, baseOptions, dateTicks } from "@/utils/dashboardCharts";
import WidgetCard from "./WidgetCard.vue";

const PERIOD_FORMATS = {
  monthly: "YYYY-MM",
  daily: "YYYY-MM-DD",
  hourly: "YYYY-MM-DD HH:mm",
};

export default {
  name: "DashFinancial",
  components: { WidgetCard },
  props: { data: { type: Object, required: true } },
  data() {
    return {
      stripeGranularity: "monthly",
      granularities: [
        { id: "monthly", label: "Mensal" },
        { id: "daily", label: "Diário" },
        { id: "hourly", label: "Horário" },
      ],
      moneyOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ ticks: dateTicks }],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: (v) => moneyBR(v),
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (item, d) => {
              const ds = d.datasets[item.datasetIndex];
              return `${ds.label}: ${moneyBR(item.yLabel)}`;
            },
          },
        },
      },
      // Lucro pode ser negativo (estornos > cobranças no período) — não
      // pode travar o eixo Y em zero, senão o gráfico mente.
      stripeMoneyOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ ticks: dateTicks }],
          yAxes: [{ ticks: { callback: (v) => moneyBR(v) } }],
        },
        tooltips: {
          callbacks: {
            label: (item, d) => {
              const ds = d.datasets[item.datasetIndex];
              return `${ds.label}: ${moneyBR(item.yLabel)}`;
            },
          },
        },
      },
    };
  },
  computed: {
    mrr() {
      const rows = this.data.mrr_timeline || [];
      return {
        labels: rows.map((r) => r.year_month),
        datasets: [
          {
            label: "MRR ativo",
            data: rows.map((r) => Number(r.mrr_active)),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 3,
            fill: false,
          },
        ],
      };
    },
    revenueProfit() {
      const rows = this.data.appointments_revenue_timeline || [];
      return {
        labels: rows.map((r) => r.year_month),
        datasets: [
          {
            label: "Faturamento (volume)",
            data: rows.map((r) => Number(r.revenue)),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 3,
            fill: false,
          },
          {
            label: "Lucro (comissão)",
            data: rows.map((r) => Number(r.profit)),
            borderColor: COLORS[3],
            backgroundColor: COLORS[3],
            pointBackgroundColor: "#fff",
            pointRadius: 3,
            fill: false,
          },
        ],
      };
    },
    syncedUntil() {
      return this.data.stripe?.synced_until || null;
    },
    syncedUntilLabel() {
      return this.syncedUntil
        ? this.$moment(this.syncedUntil).format("DD/MM/YYYY HH:mm")
        : null;
    },
    stripeSeries() {
      const rows = this.data.stripe?.[this.stripeGranularity] || [];
      const format = PERIOD_FORMATS[this.stripeGranularity];
      const syncMoment = this.syncedUntil ? this.$moment(this.syncedUntil) : null;

      // Balde depois do synced_until não tem dado importado ainda — mandar
      // null (não 0) pra virar um corte no gráfico, não uma queda falsa.
      const valueOrNull = (row, key) => {
        if (syncMoment && this.$moment(row.period, format).isAfter(syncMoment)) {
          return null;
        }
        return Number(row[key]);
      };

      return {
        labels: rows.map((r) => r.period),
        datasets: [
          {
            label: "Faturamento*",
            data: rows.map((r) => valueOrNull(r, "revenue")),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
          {
            label: "Taxa Stripe",
            data: rows.map((r) => valueOrNull(r, "stripe_fee")),
            borderColor: COLORS[4],
            backgroundColor: COLORS[4],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
          {
            label: "Lucro*",
            data: rows.map((r) => valueOrNull(r, "profit")),
            borderColor: COLORS[3],
            backgroundColor: COLORS[3],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.stripe-footnotes {
  list-style: none;
  padding-left: 0;
}
.stripe-footnotes li {
  border-left: 3px solid #e3e8ef;
  padding-left: 10px;
  margin-bottom: 6px;
}
.stripe-footnotes li:last-child {
  margin-bottom: 0;
}
</style>
